/**
 * ProjectNotes - Notes equipe + Comptes rendus de reunion (Iter 105).
 *
 * 2 types de notes :
 *  - "note"    : commentaire libre avec @mentions et creation auto de taches
 *  - "meeting" : compte rendu structure (date, titre, type, participants, contenu)
 *
 * Layout responsive compact (mobile-first) : cards denses, badges courts, expand au tap.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MentionTextarea, useMentionRenderer } from './MentionTextarea';
import { useTeamHandles } from '../hooks/useTeamHandles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TODO_RE = /\b(TODO|todo|todo:|merci de|peux-tu|pourrais-tu|peux tu|pourrais tu)\b/i;

const MEETING_TYPES = [
  { value: 'chantier', label: 'Chantier', emoji: '🏗️', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'client',   label: 'Client',   emoji: '👤', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'equipe',   label: 'Equipe',   emoji: '👥', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'autre',    label: 'Autre',    emoji: '📌', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];
const typeMeta = (t) => MEETING_TYPES.find((m) => m.value === t) || MEETING_TYPES[3];

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export const ProjectNotes = ({ projectId }) => {
  const teamHandles = useTeamHandles();
  const renderer = useMentionRenderer();
  const handleNames = teamHandles.map((h) => h.handle).concat(['ceo', 'projets', 'admin']);
  const HANDLE_RE = new RegExp(`@(${handleNames.join('|')})\\b`, 'i');
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState({});
  // Iter 105 : filtre + mode meeting
  const [filter, setFilter] = useState('all'); // all | note | meeting
  const [mode, setMode] = useState('note');     // note | meeting (compose)
  // Champs communs
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [createTask, setCreateTask] = useState(false);
  const [taskDue, setTaskDue] = useState('');
  // Champs meeting
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [meetingType, setMeetingType] = useState('chantier');
  const [participantsRaw, setParticipantsRaw] = useState('');
  // Iter 111 : option de partage immediat au client du compte rendu
  const [shareToClient, setShareToClient] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  // Expand state per meeting
  const [expanded, setExpanded] = useState({});

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/project-notes/${projectId}`);
      setNotes(r.data?.notes || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [projectId]);

  const fetchTasks = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tasks?project_id=${projectId}&include_done=true`);
      setTasks(r.data?.tasks || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    fetchNotes();
    fetchTasks();
    axios.get(`${API}/auth/me`).then((r) => setMe(r.data || {})).catch(() => {});
  }, [fetchNotes, fetchTasks]);

  useEffect(() => {
    if (mode !== 'note') { setCreateTask(false); return; }
    const m = text.match(HANDLE_RE);
    const has = TODO_RE.test(text);
    setCreateTask(!!(m && has));
  }, [text, mode]);

  const reset = () => {
    setText('');
    setTaskDue('');
    setMeetingTitle('');
    setMeetingDate(new Date().toISOString().slice(0, 10));
    setMeetingType('chantier');
    setParticipantsRaw('');
    setShareToClient(false);
  };

  const shareNoteToClient = async (noteId) => {
    if (!window.confirm('Envoyer ce compte rendu par email au client maintenant ?')) return;
    setSharingId(noteId);
    try {
      const r = await axios.post(`${API}/project-notes/${noteId}/share-to-client`);
      alert(`Compte rendu envoye a ${r.data.client_email}`);
      // refresh local state
      setNotes((prev) => prev.map((n) => n.note_id === noteId
        ? { ...n, shared_to_client: true, shared_to_client_at: r.data.shared_at, shared_to_client_email: r.data.client_email }
        : n));
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSharingId(null);
    }
  };

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    if (mode === 'meeting' && !meetingTitle.trim()) {
      alert('Le titre de la reunion est obligatoire');
      return;
    }
    setPosting(true);
    try {
      const payload = mode === 'meeting' ? {
        text: t,
        note_type: 'meeting',
        meeting_title: meetingTitle.trim(),
        meeting_date: meetingDate || null,
        meeting_type: meetingType,
        participants: participantsRaw.split(',').map((p) => p.trim()).filter(Boolean),
      } : { text: t, note_type: 'note' };
      const r = await axios.post(`${API}/project-notes/${projectId}`, payload);
      setNotes([r.data, ...notes]);
      // Iter 111 : partage immediat du compte rendu au client si demande
      if (mode === 'meeting' && shareToClient && r.data?.note_id) {
        try {
          const sr = await axios.post(`${API}/project-notes/${r.data.note_id}/share-to-client`);
          setNotes((prev) => prev.map((n) => n.note_id === r.data.note_id
            ? { ...n, shared_to_client: true, shared_to_client_at: sr.data.shared_at, shared_to_client_email: sr.data.client_email }
            : n));
          alert(`Compte rendu envoye a ${sr.data.client_email}`);
        } catch (e2) {
          alert("Compte rendu enregistre mais email non envoye : " + (e2.response?.data?.detail || e2.message));
        }
      }
      if (mode === 'note' && createTask) {
        const handle = (text.match(HANDLE_RE) || [])[1];
        if (handle) {
          const title = t.replace(/@\w+/g, '').trim().slice(0, 120) || 'Tache assignee';
          await axios.post(`${API}/tasks`, {
            title, description: t, assigned_to: handle,
            project_id: projectId, due_date: taskDue || null,
          }).catch(() => {});
          fetchTasks();
        }
      }
      reset();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    } finally { setPosting(false); }
  };

  const removeNote = async (note_id) => {
    if (!window.confirm('Supprimer cet element ?')) return;
    try {
      await axios.delete(`${API}/project-notes/${note_id}`);
      setNotes(notes.filter((n) => n.note_id !== note_id));
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const toggleTask = async (t) => {
    try { await axios.patch(`${API}/tasks/${t.task_id}`, { done: !t.done }); fetchTasks(); }
    catch { /* ignore */ }
  };
  const removeTask = async (t) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try { await axios.delete(`${API}/tasks/${t.task_id}`); fetchTasks(); }
    catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const filtered = notes.filter((n) => filter === 'all' || (n.note_type || 'note') === filter);
  const meetingCount = notes.filter((n) => n.note_type === 'meeting').length;
  const noteCount = notes.filter((n) => (n.note_type || 'note') === 'note').length;

  return (
    <div className="realease-card p-4 sm:p-6" data-testid="project-notes">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
          <svg className="w-5 h-5 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          Notes &amp; Comptes rendus
        </h3>
        <span className="text-[11px] text-gray-400">{noteCount} note(s) · {meetingCount} CR · {tasks.filter((t) => !t.done).length} tache(s) ouverte(s)</span>
      </div>

      {/* Filtre */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-3 text-xs">
        {[
          { v: 'all', l: 'Tout' },
          { v: 'note', l: 'Notes' },
          { v: 'meeting', l: 'Comptes rendus' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1 rounded font-medium transition flex-1 ${filter === f.v ? 'bg-white shadow text-[#13736D]' : 'text-gray-600 hover:text-gray-800'}`} data-testid={`project-notes-filter-${f.v}`}>
            {f.l}
          </button>
        ))}
      </div>

      {/* Selecteur mode compose */}
      <div className="flex items-center gap-1 mb-2 text-xs">
        <button
          onClick={() => setMode('note')}
          className={`px-3 py-1 rounded-full font-medium border ${mode === 'note' ? 'bg-[#008A8D] text-white border-[#008A8D]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
          data-testid="project-notes-mode-note"
        >+ Note libre</button>
        <button
          onClick={() => setMode('meeting')}
          className={`px-3 py-1 rounded-full font-medium border ${mode === 'meeting' ? 'bg-[#13736D] text-white border-[#13736D]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
          data-testid="project-notes-mode-meeting"
        >+ Compte rendu reunion</button>
      </div>

      {/* Champs meeting */}
      {mode === 'meeting' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2 p-3 rounded-lg bg-[#13736D]/5 border border-[#13736D]/20" data-testid="project-notes-meeting-fields">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-gray-600 block mb-0.5">Titre de la reunion</label>
            <input
              type="text" value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)}
              placeholder="Ex : Reunion chantier S07 / Point client / Revue equipe"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="meeting-title"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-600 block mb-0.5">Date</label>
            <input
              type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="meeting-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-600 block mb-0.5">Type</label>
            <select
              value={meetingType} onChange={(e) => setMeetingType(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="meeting-type"
            >
              {MEETING_TYPES.map((m) => <option key={m.value} value={m.value}>{m.emoji} {m.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-gray-600 block mb-0.5">Participants (separes par virgule)</label>
            <input
              type="text" value={participantsRaw} onChange={(e) => setParticipantsRaw(e.target.value)}
              placeholder="Jean, Julie, BTP Bretagne, Plomberie Test SAS..."
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="meeting-participants"
            />
          </div>
          {/* Iter 111 : option de partage immediat au client */}
          <div className="col-span-1 sm:col-span-3 flex items-center gap-2 mt-1 p-2 bg-blue-50 border border-blue-200 rounded">
            <input
              type="checkbox"
              id="share-to-client-cb"
              checked={shareToClient}
              onChange={(e) => setShareToClient(e.target.checked)}
              className="w-4 h-4 accent-[#008A8D] cursor-pointer"
              data-testid="meeting-share-to-client"
            />
            <label htmlFor="share-to-client-cb" className="text-xs text-blue-900 cursor-pointer">
              <b>Partager au client</b> par email a l'enregistrement (Resend)
            </label>
          </div>
        </div>
      )}

      <MentionTextarea
        value={text} onChange={setText}
        rows={mode === 'meeting' ? 5 : 3}
        placeholder={mode === 'meeting'
          ? "Contenu du compte rendu : decisions prises, points abordes, actions a mener... Astuce : '@julie merci de...' cree une tache."
          : "Ajouter une note. Tapez @ pour mentionner. Astuce : '@julie merci de verifier...' cree une tache."}
        testid="project-note-input"
      />

      {createTask && mode === 'note' && (
        <div className="mt-2 p-2 bg-[#FDF8F0] border border-[#E8DCC4] rounded-lg flex items-center gap-2 text-xs flex-wrap" data-testid="create-task-hint">
          <span className="text-[#A57F4F] font-semibold flex items-center gap-1">✓ Tache assignee</span>
          <label className="text-gray-600">Echeance :</label>
          <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)}
                 className="border border-gray-200 rounded px-2 py-0.5" data-testid="create-task-due" />
        </div>
      )}

      <div className="mt-2 flex justify-end items-center">
        <button onClick={add} disabled={posting || !text.trim()}
                className={`px-4 py-1.5 text-white text-sm font-semibold rounded-lg disabled:opacity-50 ${mode === 'meeting' ? 'bg-[#13736D] hover:bg-[#0e5d58]' : 'bg-[#008A8D] hover:bg-[#006B6D]'}`}
                data-testid="project-note-submit">
          {posting ? 'Enregistrement...' : (mode === 'meeting' ? '+ Enregistrer le CR' : (createTask ? '+ Note + Tache' : '+ Ajouter'))}
        </button>
      </div>

      {/* Section taches */}
      {tasks.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100" data-testid="project-tasks-section">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Taches du projet ({tasks.filter((t) => !t.done).length} ouverte(s))</p>
          <div className="space-y-1">
            {tasks.map((t) => (
              <div key={t.task_id} className={`px-2 py-1 rounded flex items-center gap-2 text-xs ${t.done ? 'bg-gray-50 opacity-60' : 'bg-white border border-gray-200'}`} data-testid={`project-task-${t.task_id}`}>
                <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(t)} className="w-3.5 h-3.5 accent-[#008A8D] cursor-pointer" />
                <span className={`flex-1 truncate ${t.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{t.title}</span>
                <span className="text-[10px] text-gray-500 hidden sm:inline">@{t.assigned_to_name || t.assigned_to_email}</span>
                {t.due_date && <span className="text-[10px] text-[#A57F4F]">{t.due_date}</span>}
                <button onClick={() => removeTask(t)} className="text-gray-300 hover:text-red-500 text-[10px]">✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste notes + CR */}
      <div className="mt-4 space-y-2">
        {loading && <p className="text-xs text-gray-400 italic">Chargement...</p>}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-3">Aucun element dans cette categorie.</p>
        )}
        {filtered.map((n) => {
          const canDelete = n.author_id === me.user_id || ['admin', 'ceo'].includes(me.role);
          const isMeeting = n.note_type === 'meeting';
          const isExpanded = expanded[n.note_id] !== false; // default open
          const tm = isMeeting ? typeMeta(n.meeting_type) : null;
          if (isMeeting) {
            return (
              <div key={n.note_id} className="border-l-4 border-[#13736D] bg-white rounded-r-lg shadow-sm" data-testid={`project-note-${n.note_id}`}>
                <button
                  onClick={() => setExpanded((s) => ({ ...s, [n.note_id]: !isExpanded }))}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 flex-wrap hover:bg-gray-50 rounded-r-lg"
                  data-testid={`project-meeting-toggle-${n.note_id}`}
                >
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${tm.color}`}>{tm.emoji} {tm.label.toUpperCase()}</span>
                  <span className="text-sm font-semibold text-gray-900 truncate flex-1">{n.meeting_title || '(Sans titre)'}</span>
                  <span className="text-[11px] text-gray-500">{formatDate(n.meeting_date)}</span>
                  <span className="text-gray-400 text-xs">{isExpanded ? '▾' : '▸'}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    {n.participants && n.participants.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mb-2">
                        <span className="text-[10px] uppercase font-semibold text-gray-500">Participants :</span>
                        {n.participants.map((p, i) => (
                          <span key={i} className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{p}</span>
                        ))}
                      </div>
                    )}
                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{renderer(n.text)}</div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 flex-wrap gap-2">
                      <p className="text-[10px] text-gray-400">Redige par <b>{n.author_name}</b> · {formatDateTime(n.created_at)}</p>
                      <div className="flex items-center gap-2">
                        {n.shared_to_client ? (
                          <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid={`project-meeting-shared-${n.note_id}`}>
                            ✓ Envoye au client {n.shared_to_client_email ? `(${n.shared_to_client_email})` : ''}
                          </span>
                        ) : (
                          <button
                            onClick={() => shareNoteToClient(n.note_id)}
                            disabled={sharingId === n.note_id}
                            className="text-[10px] text-[#008A8D] border border-[#008A8D] hover:bg-[#008A8D] hover:text-white px-2 py-0.5 rounded disabled:opacity-50"
                            data-testid={`project-meeting-share-${n.note_id}`}
                          >
                            {sharingId === n.note_id ? 'Envoi…' : 'Partager au client'}
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => removeNote(n.note_id)} className="text-red-400 hover:text-red-600 text-[10px]" data-testid={`project-meeting-delete-${n.note_id}`}>Supprimer</button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          }
          // Note libre - rendering compact
          return (
            <div key={n.note_id} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg" data-testid={`project-note-${n.note_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-[10px] text-gray-500">
                  <b className="text-gray-800">{n.author_name}</b> · {formatDateTime(n.created_at)}
                </div>
                {canDelete && (
                  <button onClick={() => removeNote(n.note_id)} className="text-red-400 hover:text-red-600 text-[10px]">Supprimer</button>
                )}
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-snug">{renderer(n.text)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectNotes;
